import { Card } from "./ui/card";
import { Timer, Filter, Euro, Hourglass, ShieldAlert } from "lucide-react";

const eur = (v) => `${Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })} €`;

function Section({ icon: Icon, title, desc, children, testid }) {
  return (
    <Card className="rounded border-border p-5" data-testid={testid}>
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <h3 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold">{title}</h3>
        </div>
        {desc && <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed" data-testid={`${testid}-desc`}>{desc}</p>}
      </div>
      {children}
    </Card>
  );
}

const TEMPI = [
  ["primo_contatto", "Velocità di risposta", "lead → primo contatto", "Dall'ingresso del lead alla prima chiamata, WhatsApp, email, appuntamento o sopralluogo."],
  ["qualifica", "Lead → qualificato", "profilazione", "Dall'ingresso del lead alla profilazione (qualifica)."],
  ["meet", "Qualificato → meet", "prenotazione sopralluogo", "Dalla qualifica alla prenotazione del meet/sopralluogo."],
  ["meet_preventivo", "Meet → preventivo", "invio proposta", "Dal meet all'invio della proposta al cliente."],
  ["apertura_preventivo", "Preventivo → apertura", "lettura cliente", "Dall'invio della proposta alla prima apertura del link da parte del cliente."],
  ["ciclo_vendita", "Ciclo di vendita", "lead → contratto", "Dall'ingresso del lead alla firma del contratto."],
];

export function TempiKpi({ tempi }) {
  return (
    <Section icon={Timer} title="Tempi (mediana)" testid="dash-tempi" desc="Tempo tipico (mediana: metà dei casi è più veloce, metà più lenta) tra due passaggi del processo, per i passaggi avvenuti nel periodo selezionato. «Casi» = numero di lead su cui è calcolato.">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {TEMPI.map(([k, label, sub, help]) => {
          const t = tempi[k] || {};
          return (
            <div key={k} className="rounded border border-border p-3" data-testid={`tempo-${k}`}>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">{label}</div>
              <div className="font-heading font-bold text-2xl mt-1">{t.mediana || "—"}</div>
              <div className="text-[11px] text-muted-foreground">{sub} · {t.n || 0} casi</div>
              <div className="text-[10.5px] text-muted-foreground/80 mt-1 leading-snug">{help}</div>
              {k === "primo_contatto" && t.n > 0 && (
                <div className="text-[11px] mt-1"><span className="text-primary font-semibold">{t.entro_30m}%</span> entro 30 min · <span className="text-primary font-semibold">{t.entro_24h}%</span> entro 24h</div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

export function FunnelValore({ funnel, valore }) {
  const max = Math.max(1, ...funnel.map((f) => f.count));
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2">
        <Section icon={Filter} title="Funnel del periodo" testid="dash-funnel" desc="Dei lead commerciali entrati nel periodo: quanti sono stati qualificati, hanno fatto un meet, ricevuto un preventivo e firmato il contratto. La percentuale è sul totale dei lead entrati.">
          <div className="space-y-2">
            {funnel.map((f) => (
              <div key={f.key} className="flex items-center gap-3 text-sm" data-testid={`funnel-${f.key}`}>
                <span className="w-24 shrink-0 text-muted-foreground">{f.label}</span>
                <div className="flex-1 h-6 rounded bg-secondary overflow-hidden">
                  <div className="h-full bg-primary/80 transition-[width] duration-500" style={{ width: `${(100 * f.count) / max}%` }} />
                </div>
                <span className="w-20 text-right font-heading font-bold">{f.count} <span className="text-xs text-muted-foreground font-normal">{f.pct}%</span></span>
              </div>
            ))}
          </div>
        </Section>
      </div>
      <Section icon={Euro} title="Valore" testid="dash-valore" desc="Prezzo IVA inclusa dei preventivi. Pipeline aperta = somma dei lead non ancora a contratto; ponderata = stessa somma pesata per la probabilità della fase (Lead 5%, Profilazione 10%, Consulenza 25%, Preventivo 50%); contratti = firmati nel periodo.">
        <div className="space-y-3">
          {[["Pipeline aperta", valore.pipeline, "valore-pipeline"], ["Pipeline ponderata", valore.ponderato, "valore-ponderato"], ["Contratti nel periodo", valore.contratti_periodo, "valore-contratti"]].map(([l, v, id]) => (
            <div key={id} data-testid={id}>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">{l}</div>
              <div className="font-heading font-bold text-xl">{eur(v)}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

export function AgingQualita({ aging, qualita, onOpen }) {
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2">
        <Section icon={Hourglass} title="Anzianità pipeline aperta" testid="dash-aging" desc="Per ogni fase: numero di lead aperti e da quanti giorni (mediana) sono entrati nel CRM. Sotto, i 3 lead più vecchi della fase: cliccali per aprirli.">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {aging.map((a) => (
              <div key={a.stage} className="rounded border border-border p-3" data-testid={`aging-${a.stage}`}>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">{a.label} · {a.count}</div>
                <div className="font-heading font-bold text-2xl mt-1">{a.mediana_giorni != null ? `${a.mediana_giorni} g` : "—"}</div>
                <div className="text-[11px] text-muted-foreground">mediana dall'ingresso</div>
                {a.piu_vecchi.map((r) => <button key={r.id} onClick={() => onOpen(r.id)} className="block w-full text-left text-[11px] truncate hover:text-primary transition-colors">{r.name || "—"} · {r.giorni} g</button>)}
              </div>
            ))}
          </div>
        </Section>
      </div>
      <Section icon={ShieldAlert} title="Qualità dati" testid="dash-qualita" desc="Lead da sistemare: senza un operatore assegnato o con stessa email/telefono di un altro lead. Clicca per vedere l'elenco.">
        {[["senza_assegnatario", "Lead senza assegnatario"], ["duplicati", "Possibili duplicati (email/telefono)"]].map(([k, l]) => (
          <button key={k} onClick={() => onOpen(null, { label: l, leads: qualita[k].leads })} className="w-full flex items-center justify-between rounded border border-border px-3 py-2 mb-2 text-sm hover:border-primary/50 transition-colors" data-testid={`qualita-${k}`}>
            <span>{l}</span><span className={`font-heading font-bold ${qualita[k].count ? "text-amber-500" : "text-emerald-500"}`}>{qualita[k].count}</span>
          </button>
        ))}
      </Section>
    </div>
  );
}
